import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap, ShieldCheck, MessageCircle } from "lucide-react";
import DomainForm from "@/components/funnel/DomainForm";
import LiveDemoChat from "@/components/landing/LiveDemoChat";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

export const Hero = ({ onAnalyze, loading }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative av-grid-bg border-b-2 border-obsidian overflow-hidden">
      <div className="av-container grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-14 md:py-24">
        <motion.div style={{ y: yText, opacity }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 border-2 border-obsidian px-3 py-1.5 mb-6">
            <Zap className="h-3.5 w-3.5 text-conversion" />
            <span className="font-data text-[11px] uppercase tracking-[0.2em] text-obsidian">{t("hero.badge")}</span>
          </div>
          {HE ? (
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-obsidian">
              הלקוחות שלכם שואלים <span className="whitespace-nowrap">את ה-AI.</span><br />
              <span className="whitespace-nowrap">ה-AI</span> <span className="text-conversion whitespace-nowrap">ממליץ עליכם?</span>
            </h1>
          ) : (
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-obsidian">
              Your customers ask&nbsp;AI.<br />
              Does AI <span className="text-conversion whitespace-nowrap">recommend you?</span>
            </h1>
          )}
          {HE ? (
            <p className="mt-6 text-base md:text-xl text-neutral-700 leading-relaxed max-w-xl">
              אם ה-AI לא ממליץ על העסק שלכם — הוא <strong className="text-obsidian">שולח את הלקוחות שלכם למתחרה</strong>.
              בדקו איפה אתם עומדים — <strong className="text-conversion">בחינם, תוך 30 שניות</strong>.
            </p>
          ) : (
            <p className="mt-6 text-base md:text-xl text-neutral-700 leading-relaxed max-w-xl">
              If it doesn&apos;t, it&apos;s <strong className="text-obsidian">sending your customers to a competitor</strong>.
              Find out — <strong className="text-conversion">free, in 30 seconds</strong>.
            </p>
          )}

          <div className="mt-8 max-w-xl">
            <DomainForm onAnalyze={onAnalyze} loading={loading} testid="hero-domain-form" />
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-neutral-600">
              <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-conversion" /> {t("hero.instant")}</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-data-positive" /> {t("hero.noSignup")}</span>
              <span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> {t("hero.whatsapp")}</span>
            </div>
          </div>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yCard }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <LiveDemoChat />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
